/**
 * V2Routes — the child <Route>s rendered under <V2Layout/> when V2 is active.
 *
 * Every route currently points at <V2Placeholder/>. Later waves swap
 * individual routes to their real V2 pages — keep this one <Route> per line
 * for easy editing.
 */

import type { ReactNode } from 'react';
import { Navigate, Route } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import V2DashboardPage from './pages/V2DashboardPage';
import V2EstimateDetailPage from './pages/V2EstimateDetailPage';
import V2SupplementPage from './pages/V2SupplementPage';
import V2ScopeBuilderPage from './pages/V2ScopeBuilderPage';
import V2DesignSystemPage from './pages/V2DesignSystemPage';
import V2ChatPage from './pages/V2ChatPage';
import V2SettingsPage from './pages/V2SettingsPage';
import V2PricingPage from './pages/V2PricingPage';
import V2PartnersPage from './pages/V2PartnersPage';
import V2McpProposalsPage from './pages/V2McpProposalsPage';
import V2McpAuthorizePage from './pages/V2McpAuthorizePage';
import V2AssistantPage from './pages/V2AssistantPage';
import V2MacrosPage from './pages/V2MacrosPage';
import V2WelcomePage from './pages/V2WelcomePage';
import V2PlanPage from './pages/V2PlanPage';
// DIFF-2 comparison pages are layout-agnostic (self-contained max-w containers),
// so they render directly inside V2Layout — no V2-specific variant needed yet.
import ComparisonsPage from '../pages/ComparisonsPage';
import ComparisonReportPage from '../pages/ComparisonReportPage';
import LockedFeature from './components/LockedFeature';
import { useIsMember } from '../hooks/useMembership';

/**
 * Renders children only for members (role-holding users); pay-per-conversion
 * accounts see the LockedFeature card instead. The backend enforces the same
 * lock server-side — this is the UX half.
 */
export function RequireMember({
  feature,
  description,
  children,
}: {
  feature: string;
  description?: string;
  children: ReactNode;
}) {
  const isMember = useIsMember();
  if (!isMember) return <LockedFeature feature={feature} description={description} />;
  return <>{children}</>;
}

/**
 * Renders children only for users holding one of the given roles; everyone
 * else is sent back to the dashboard. Waits for roles to load so a slow
 * roles fetch doesn't redirect a legitimate admin.
 */
export function RequireAnyRole({
  roles: required,
  children,
}: {
  roles: string[];
  children: ReactNode;
}) {
  const roles = useAuthStore((s) => s.roles);
  const rolesLoaded = useAuthStore((s) => s.rolesLoaded);
  if (!rolesLoaded) return null;
  if (!required.some((r) => roles.includes(r))) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Returns the V2 child routes (to be spread inside a layout <Route>).
 *
 * @returns The V2 <Route> elements.
 */
export function v2Routes() {
  return (
    <>
      <Route path="/" element={<V2DashboardPage />} />
      <Route path="/project/:id" element={<V2EstimateDetailPage />} />
      <Route path="/completeness" element={<V2SupplementPage />} />
      {/* PRP-MCP-10 G5: open to every signed-in user. The page shows the subscribe line to a
          free account and the server (comparison_access) is the gate: Pro, Pro Plus, trial,
          grant and admin, three comparisons per account (admin unlimited). */}
      <Route path="/comparisons" element={<ComparisonsPage />} />
      <Route path="/comparisons/:id" element={<ComparisonReportPage />} />
      <Route
        path="/scope/builder"
        element={
          <RequireMember
            feature="Scope Builder"
            description="AI scope generation from field notes is part of the full plan."
          >
            <V2ScopeBuilderPage />
          </RequireMember>
        }
      />
      <Route
        path="/v2/design"
        element={
          <RequireAnyRole roles={['super_admin']}>
            <V2DesignSystemPage />
          </RequireAnyRole>
        }
      />
      <Route
        path="/chat"
        element={
          <RequireMember
            feature="AI Chat"
            description="The estimating assistant chat is part of the full plan."
          >
            <V2ChatPage />
          </RequireMember>
        }
      />
      <Route
        path="/chat/:projectId"
        element={
          <RequireMember
            feature="AI Chat"
            description="The estimating assistant chat is part of the full plan."
          >
            <V2ChatPage />
          </RequireMember>
        }
      />
      {/* Admin + rule screens (/admin, /admin/*, /review/*) are NOT declared
          here — they render in the classic MainLayout shell even while V2 is
          active (see App.tsx route split + isClassicShellRoute). */}
      <Route path="/profile" element={<V2SettingsPage />} />
      {/* Pricing + org partnership — accessible to any logged-in user (not member-gated). */}
      <Route path="/pricing" element={<V2PricingPage />} />
      <Route path="/partners" element={<V2PartnersPage />} />
      {/* PRP-MCP-10: connect a chat app (open to free users) and the post-checkout next steps. */}
      <Route path="/assistant" element={<V2AssistantPage />} />
      {/* PRP-MCP-12: the user's own macros (open to free users). */}
      <Route path="/macros" element={<V2MacrosPage />} />
      <Route path="/welcome" element={<V2WelcomePage />} />
      {/* PRP-MCP-1: review chat-app proposals + OAuth consent (any logged-in user;
          the backend enforces plan entitlement). */}
      <Route path="/mcp/proposals" element={<V2McpProposalsPage />} />
      <Route path="/mcp/authorize" element={<V2McpAuthorizePage />} />
      <Route path="/project/:id/plan" element={<V2PlanPage />} />
    </>
  );
}
